import { useLocalParticipant, usePersistentUserChoices } from "@livekit/components-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "@shared/ui/modal/modal";
import { Tooltip } from "react-tooltip";
import { getNoiseSuppressionFallback } from "../../lib/get-noise-suppression-fallback";
import { getSupportedAudioSettings } from "../../lib/get-supported-audio-settings";
import { getSupportedConstraints } from "../../lib/get-supported-constraints";
import { useConferenceSettingsStore } from "../../model/conference-settings.store";
import { useConferenceSettingsAudio } from "../../model/use-conference-settings-audio";
import { useConferenceSettingsModal } from "../../model/use-conference-settings-modal";
import { ConferenceSettingsContent } from "./conference-settings-content";
import { ConferenceSettingsSidebar } from "./conference-settings-sidebar";
import s from "./conference-settings-modal.module.scss";

export const ConferenceSettingsModal = () => {
  const { isOpen, close } = useConferenceSettingsModal();
  const {
    selectedProfile,
    setSelectedProfile,
    selectedAudioBitrate,
    setSelectedAudioBitrate,
    selectedAudioOutputDeviceId,
    setSelectedAudioOutputDeviceId,
    audioSettings,
    setAudioSettings,
    deviceLevels,
    setDeviceLevels,
  } = useConferenceSettingsStore();
  const {
    userChoices,
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices();
	
  const { localParticipant, microphoneTrack, isMicrophoneEnabled, lastMicrophoneError } =
    useLocalParticipant();
  const [activeSection, setActiveSection] = useState("sound");
  const signalSettingsForm = useForm({
    mode: "onChange",
    defaultValues: audioSettings,
  });

  const supportedConstraints = useMemo(() => getSupportedConstraints(), []);
  const noiseSuppressionFallback = useMemo(() => getNoiseSuppressionFallback(), []);
  const availableAudioSettings = useMemo(
    () => getSupportedAudioSettings(supportedConstraints),
    [supportedConstraints]
  );

  const {
    hasAdvancedAudioSettings,
    isApplyingAudio,
    audioSettingsError,
    applyProfile,
  } = useConferenceSettingsAudio({
    audioSettings,
    setAudioSettings,
    microphoneVolume: deviceLevels.microphone,
    selectedAudioInputDeviceId: userChoices.audioDeviceId,
    selectedProfile,
    setSelectedProfile,
    signalSettingsForm,
    isOpen,
    supportedConstraints,
    localParticipant,
    microphoneTrack,
    isMicrophoneEnabled,
  });

  const soundSectionProps = {
    microphoneTrack: microphoneTrack?.track,
    hasAdvancedAudioSettings,
    selectedProfile,
    applyProfile,
    signalSettingsForm,
    supportedConstraints,
    echoCancellationEnabled: audioSettings.echoCancellation,
    selectedAudioBitrate,
    setSelectedAudioBitrate,
    selectedAudioOutputDeviceId,
    setSelectedAudioOutputDeviceId,
    isApplyingAudio,
    audioSettingsError,
    lastMicrophoneError,
  };

  const setMicrophoneVolume = (nextValue) => {
    setDeviceLevels((prev) => ({
      ...prev,
      microphone: nextValue,
    }));
  };

  const setSpeakerVolume = (nextValue) => {
    setDeviceLevels((prev) => ({
      ...prev,
      speaker: nextValue,
    }));
  };

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const applyVolume = () => {
      document.querySelectorAll("audio, video").forEach((node) => {
        // Элементы синхронного аудио (музыка/метроном) управляются своим
        // стором (jukebox-volume.store) — не затираем их громкость
        // глобальной настройкой динамика, иначе регулятор музыки «слетает».
        if (node.dataset?.jukeboxVolume !== undefined) return;
        // Превью-плееры синхронного аудио (react-h5-audio-player) беззвучны
        // по замыслу: звук ведущего идёт через Web Audio-мониторинг. Библиотека
        // глушит их один раз при маунте (volume = 0), а глобальная настройка
        // динамика поднимала громкость обратно — слышно дубль поверх эфира.
        if (node.closest?.(".rhap_container")) return;
        node.volume = deviceLevels.speaker / 100;
      });
    };

    applyVolume();

    // LiveKit подключает audio/video элементы динамически (RoomAudioRenderer и т.п.) —
    // отслеживаем появление новых, иначе громкость динамика на них сбрасывалась на 100%
    let frameId = 0;
    const scheduleApply = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(applyVolume);
    };
    const observer = new MutationObserver(scheduleApply);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [deviceLevels.speaker]);

  const supportSectionProps = {
    availableAudioSettings,
    lastMicrophoneError,
    supportedConstraints,
    noiseSuppressionFallback,
  };

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      style={{
        content: {
          maxWidth: 860,
          inset: "50% auto auto 50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 32px)",
          height: "min(541px, calc(100svh - 32px))",
          overflow: "hidden",
          padding: 0,
        },
      }}
    >
      <div className={s.modalShell}>
        <ConferenceSettingsSidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />

        <div className={s.contentPanel}>
          <div className={s.contentCard}>
            <ConferenceSettingsContent
              activeSection={activeSection}
              userChoices={userChoices}
              saveAudioInputDeviceId={saveAudioInputDeviceId}
              saveAudioInputEnabled={saveAudioInputEnabled}
              saveVideoInputDeviceId={saveVideoInputDeviceId}
              saveVideoInputEnabled={saveVideoInputEnabled}
              microphoneVolume={deviceLevels.microphone}
              speakerVolume={deviceLevels.speaker}
              setMicrophoneVolume={setMicrophoneVolume}
              setSpeakerVolume={setSpeakerVolume}
              soundSectionProps={soundSectionProps}
              supportSectionProps={supportSectionProps}
            />
          </div>
        </div>
      </div>
      <Tooltip id="tooltip-sample-rate" />
      <Tooltip id="tooltip-sample-size" />
    </Modal>
  );
};
